import { memo } from 'react';
import TeamLogoImage from './TeamLogoImage';

/**
 * The draft board: one column per manager, one row per round.
 *
 * This is the view of the draft that survives the draft — it stays on the
 * draft room tab all season, so it has to read as a record as well as a
 * live board.
 *
 * Snake order is not encoded in the layout. A manager keeps the same column
 * for the whole draft and each cell is looked up by (manager, round), so
 * round 2 reading right-to-left is a property of the pick numbers inside the
 * cells rather than of the grid. Reversing the columns every other row was
 * the obvious alternative and it makes a manager's roster impossible to read
 * down a column, which is the thing people actually want from this.
 *
 * Eight columns never fit a 390px phone, so the grid scrolls horizontally
 * inside its own container with the round label pinned left.
 */
function DraftBoardGrid({
  draftOrder = [],
  picks = [],
  teams = [],
  memberMap = {},
  memberByMemberId = {},
  totalRounds = 7,
  currentPickIndex = null,
  currentUserId = null,
  canEnterPicks = false,
  onEditPick = null,
}) {
  // (user_id, round) -> pick. draft_picks carries member_id, so resolve it
  // back to the user_id the draft_order is keyed by.
  const byCell = picks.reduce((acc, p) => {
    const uid = memberByMemberId[p.member_id]?.userId;
    if (!uid) return acc;
    acc[`${uid}:${p.round}`] = p;
    return acc;
  }, {});

  const nManagers = draftOrder.length;
  const rounds = Array.from({ length: totalRounds }, (_, i) => i + 1);

  // Which cell is on the clock, so the live board shows where play is.
  const activeUid =
    currentPickIndex != null && nManagers > 0
      ? (() => {
          const round = Math.floor(currentPickIndex / nManagers);
          const pos =
            round % 2 === 0
              ? currentPickIndex % nManagers
              : nManagers - 1 - (currentPickIndex % nManagers);
          return draftOrder[pos];
        })()
      : null;
  const activeRound =
    currentPickIndex != null && nManagers > 0
      ? Math.floor(currentPickIndex / nManagers) + 1
      : null;

  if (nManagers === 0) {
    return (
      <div className="bg-surface-card rounded-2xl p-4 border border-hairline shadow-sm">
        <p className="text-ink-faint text-sm text-center py-4">Draft order not set yet</p>
      </div>
    );
  }

  return (
    <div className="bg-surface-card rounded-2xl border border-hairline shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-hairline">
        <h2 className="font-bold text-ink">Draft Board</h2>
        <span className="text-xs text-ink-faint">
          {picks.length} of {nManagers * totalRounds} picks
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="border-collapse">
          <thead>
            <tr>
              <th
                className="sticky left-0 z-10 bg-surface-card px-2 py-2 text-xs font-semibold text-ink-faint text-left"
                style={{ minWidth: 44 }}
              >
                Rd
              </th>
              {draftOrder.map((uid) => {
                const info = memberMap[uid];
                return (
                  <th
                    key={uid}
                    className="px-2 py-2 text-left text-white text-xs font-semibold"
                    style={{ minWidth: 120, backgroundColor: '#0072BC' }}
                  >
                    <div className="truncate">{info?.teamName ?? info?.name ?? '—'}</div>
                    {/* Manager's first name under the fantasy team name — the
                        column is identified by the person as much as the team. */}
                    <div className="text-[10px] font-normal opacity-80 truncate">
                      {uid === currentUserId ? 'You' : info?.firstName}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {rounds.map((round) => (
              <tr key={round} className="border-t border-hairline">
                <td
                  className="sticky left-0 z-10 bg-surface-card px-2 py-2 text-xs font-bold text-ink-faint"
                  style={{ minWidth: 44 }}
                >
                  {round}
                </td>

                {draftOrder.map((uid) => {
                  const pick = byCell[`${uid}:${round}`];
                  const team = pick ? teams.find((t) => t.id === pick.team_id) : null;
                  const isActiveCell = uid === activeUid && round === activeRound;

                  return (
                    <td
                      key={uid}
                      className="px-2 py-2 align-top"
                      style={{
                        minWidth: 120,
                        ...(isActiveCell
                          ? { backgroundColor: 'var(--color-band-bg)', boxShadow: 'inset 0 0 0 1px var(--color-brand-border)' }
                          : {}),
                      }}
                    >
                      {pick ? (
                        <div className="flex items-center gap-1.5">
                          <TeamLogoImage
                            teamName={team?.school}
                            primaryColor={team?.color}
                            size={18}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-medium text-ink">
                              {team?.school ?? pick.team_id}
                            </div>
                            <div className="text-[10px] text-ink-faint">
                              #{pick.pick_number}
                              {pick.auto_picked && <span className="text-warning"> auto</span>}
                            </div>
                          </div>
                          {canEnterPicks && onEditPick && (
                            <button
                              onClick={() => onEditPick(pick)}
                              className="text-[10px] text-brand hover:opacity-80 font-semibold flex-shrink-0"
                            >
                              edit
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-ink-faint opacity-60">
                          {isActiveCell ? 'on the clock' : '—'}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// The grid re-renders on any parent state change otherwise — every keystroke
// in the team search box, every elapsed-time tick — and each render rebuilds
// the cell index and 56 cells. Its props only change when a pick does.
export default memo(DraftBoardGrid);
